/**
 * Copyright © 2026 Adnuntius AS.
 */
import _ from 'lodash';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {MEDIA_TYPE, MediaTypeHelper} from '../../components/util/media-type-helper';
import {DEFAULT_LAYOUT_TYPES} from './creative-helper.service';
import {CreativeRetailProductModalService} from './creative-retail-product-modal.service';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {ASPECT_RATIOS_TRANSLATIONS} from '../admin/layout/layout-commons';
import {resolveCreativePreviewAdvertiser} from './ad/preview-request-data';
import {requiresVideoPreviewParam} from './ad/preview-language';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {LocalBulkCreativeInfo} from '../../components/session/local-bulk-creative-info';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {formatMediaTypeKey, formatMediaTypesTranslate} from '../common/filters/format-media-type';
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {findCreativeAdvertiserMismatch} from './creative-advertiser-mismatch';

export interface CreativeEditSetupParams {
  model: any;
  readOnly: boolean;
  creativeType: any;
  lineItemInfo: any;
  modelComms: any;
  $state: any;
  navigationHistory: NavigationHistoryService;
  routeParams: any;
  translate: any;
  Team: any;
  ProductFeed: any;
  CreativeHelper: any;
  adnListHelper: AdnListHelperService;
  Rollbar: any;
  Asset: any;
  Creative: any;
  LibraryCreative: any;
  LineItem: any;
  MarketplaceProduct: any;
  ExternalDemandSource: any;
  LocalNetworkProfile: any;
  LocalUserPermissions: any;
  Advertiser: any;
  TargetingEditorModalService: any;
  creativeRetailProductModalService: CreativeRetailProductModalService;
  cdr: any;
}

export function setupCreativeEditCtrl(ctrl: any, p: CreativeEditSetupParams): void {
  const {
    model, readOnly, creativeType, lineItemInfo, modelComms, $state, navigationHistory, routeParams, translate,
    Team, ProductFeed, CreativeHelper, adnListHelper, Rollbar, Asset, Creative, LibraryCreative, LineItem,
    MarketplaceProduct, ExternalDemandSource, LocalNetworkProfile, LocalUserPermissions, Advertiser,
    TargetingEditorModalService, creativeRetailProductModalService, cdr
  } = p;

  const editPhase: EditPagePhaseApi = attachEditPagePhase(ctrl, cdr);
  const mInitPromises: any[] = [];
  const mLayoutAssetMatches: Record<string, any> = {};
  const defaultLayoutType = 'OTHER';
  const prevAssetAssignments: Record<string, any> = {};
  let mAllLayoutsPromise: any;
  let mLineItemPromise: any;
  let mPreviewAdvertiserPromise: any;
  let mPreviewAdvertiserId: any;
  let suppressLineItemTransitionOnce = false;
  let marketplaceProduct: any = {};
  let timeoutPromise: any;
  let teamQuery: any;

  const reviewWorkflowTypes = ['PROPOSED', 'CREATIVE_ONLY'];

  ctrl.editsMade = () => ctrl.markExternalFormDirty();
  ctrl.updateModelHappened = () => ctrl.publishModelUpdate();

  const syncEditingModel = () => {
    ctrl.model = ctrl.editingModel;
  };

  const updateWorkflowRestrictions = (lineItem: any, workflowType: string) => {
    if (!ctrl.isSelfService) {
      return;
    }
    ctrl.isLockedDown = lineItem.userState !== 'PROPOSED' && workflowType === 'PROPOSED';
    ctrl.isCreativeContentLocked = lineItem.userState !== 'PROPOSED' && reviewWorkflowTypes.indexOf(workflowType) > -1;
    ctrl.blockSubmit = ctrl.blockSubmit || (ctrl.isNew && ctrl.isCreativeContentLocked);
  };

  ctrl.hasExternalSources = LocalNetworkInfo.obtain().getInfo('externalDemandSourceCount') > 0;
  ctrl.retailProductFields = {
    retailProductId: true,
    merchantId: true,
    title: true,
    body: true,
    image: true,
    clickUrl: true,
    sponsoredBy: true,
    priceCurrency: true,
    priceAmount: true,
    salePriceCurrency: true,
    salePriceAmount: true
  };

  ctrl.aspectRatioDescriptions = ASPECT_RATIOS_TRANSLATIONS;
  ctrl.layoutRef = {};
  ctrl.skips = ['validation.warning.creative.blocked.by.scanner'];
  ctrl.isPureMarketplace = LocalNetworkProfile.hasAdvertiserEnforced();
  ctrl.lineItemParent = lineItemInfo.fromLineItem || _.get(model, ['lineItem', 'id']);
  ctrl.creativeSetParent = lineItemInfo.fromCreativeSet || _.get(model, ['creativeSet', 'id']);
  ctrl.useCookies = true;
  if (ctrl.creativeSetParent && _.get(model, ['creativeSet', 'id'])) {
    lineItemInfo.id = lineItemInfo.id || _.get(model, ['creativeSet', 'id']);
    lineItemInfo.name = lineItemInfo.name || _.get(model, ['creativeSet', 'id']);
  }

  const doBulkUpdates = (localCreatives: any) => {
    if (ctrl.isBulkCreative) {
      return;
    }
    ctrl.bulkCreatives = localCreatives;
    ctrl.isBulkCreative = _.find(ctrl.bulkCreatives, (c: any) => c.id === model.id);
  };

  doBulkUpdates(LocalBulkCreativeInfo.obtain().getCreatives());
  doBulkUpdates(LocalBulkCreativeInfo.obtain().getLibraryCreatives());

  ctrl.defaultLayoutTypes = DEFAULT_LAYOUT_TYPES;
  ctrl.layoutTypes = ctrl.defaultLayoutTypes.concat(defaultLayoutType);

  ctrl.isSelfService = LocalUserPermissions.isSelfService();
  ctrl.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser() && !(ctrl.creativeSetParent || ctrl.fromCreativeSet);
  ctrl.needsVerificationCheck = LocalNetworkProfile.isVerificationCheckNetwork();
  ctrl.isCreative = creativeType.type === 'Creative';
  ctrl.perms = LocalUserPermissions.getAllVisPermissions();
  ctrl.creativeFields = ['name', {param: 'Layout', label: 'layout'}, {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];
  ctrl.linkedCreativeFields = ['name', {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];

  if (!ctrl.isCreative) {
    const params: any = {minimal: true};
    if (ctrl.isPureMarketplace) {
      params.filterBy = 'type';
      params.filterByValue = 'AX_ADVERTISER';
    }
    teamQuery = Team.query(params).$promise.then((page: any) => {
      ctrl.allTeams = page.results;
    });
  }

  const delayedPreview = () => {
    clearTimeout(timeoutPromise);
    timeoutPromise = setTimeout(() => callForPreview(), 1500);
  };

  if (!ctrl.isCreative && !ctrl.isNew) {
    adnListHelper.setUpBasicList(ctrl, Creative, 'fullCreativesforLibraryCreative', {
      moreParams: {libraryCreative: model.id},
      afterFill: () => {
        // Snapshot — do not bind ctrl.items (list-helper replaces that array
        // on settle and can race checkNoChanges).
        deferMarkForCheck(cdr, () => {
          ctrl.displayItems = (ctrl.items || []).slice();
          ctrl.hasItems = ctrl.displayItems.length > 0;
          ctrl.relCreativesEmpty = !ctrl.hasItems;
          ctrl.relCreatives = ctrl.displayItems;
          ctrl.relCreativesCount = ctrl.displayItems.length;
        });
      }
    });
  }

  const updateDefaultName = () => {
    CreativeHelper.updateDefaultName(ctrl);
  };

  const hydrateSelectedRetailProduct = (productId: string, merchantId: string) => {
    if (!productId || !merchantId) {
      ctrl.selRetailProduct = null;
      return Promise.resolve(null);
    }
    return ProductFeed.get({id: productId, merchantId: merchantId}).$promise.then((retailProd: any) => {
      ctrl.selRetailProduct = retailProd;
      ctrl.merchantGuide = LocalNetworkInfo.obtain().getMerchantGuide();
      ctrl.selRetailProduct.hydratedMerchant = ctrl.merchantGuide[ctrl.selRetailProduct.merchantId];
      return retailProd;
    });
  };

  const applyRetailCreativeUpdate = (creative: any) => {
    syncEditingModel();
    ctrl.editingModel = creativeType.caps === 'CREATIVE' ? new Creative(creative) : new LibraryCreative(creative);
    ctrl.model = ctrl.editingModel;
    const hydratedLayout = ctrl.layoutRef[_.get(ctrl.editingModel, ['layout', 'id'])];
    if (hydratedLayout) {
      ctrl.editingModel.layout = hydratedLayout;
    }
    suppressLineItemTransitionOnce = true;
    ctrl.saveDirect(creative);
  };

  const ensureRetailCreativeIsSaved = () => {
    if (!ctrl.isNew) {
      return Promise.resolve(ctrl.editingModel);
    }
    ctrl.onBeforeSubmit();
    return ctrl.editingModel.savePartial().then((savedCreative: any) => {
      ctrl.editingModel.id = savedCreative.id;
      _.set(ctrl.editingModel, 'validationWarnings', savedCreative.validationWarnings);
      return savedCreative;
    });
  };

  const getCurrentPreviewAdvertiser = () => {
    const modelAdvertiserId = _.get(ctrl.editingModel, ['advertiser', 'id']);
    const lineItemAdvertiserId = _.get(ctrl.lineItem, ['advertiser', 'id']);
    if (_.get(ctrl.advertiserInfo, 'id') && (_.get(ctrl.advertiserInfo, 'id') === modelAdvertiserId || _.get(ctrl.advertiserInfo, 'id') === lineItemAdvertiserId)) {
      return ctrl.advertiserInfo;
    }
    return ctrl.editingModel.advertiser || _.get(ctrl.lineItem, 'advertiser') || ctrl.advertiserInfo || null;
  };

  const getAdvertiserForPreview = () => {
    const advertiser = getCurrentPreviewAdvertiser();
    return Promise.resolve(resolveCreativePreviewAdvertiser(ctrl.editingModel, ctrl.lineItem, advertiser, (advertiserId: string) => {
      if (_.get(ctrl.advertiserInfo, 'id') === advertiserId && _.get(ctrl.advertiserInfo, 'legalName')) {
        return ctrl.advertiserInfo;
      }
      if (mPreviewAdvertiserPromise && mPreviewAdvertiserId === advertiserId) {
        return mPreviewAdvertiserPromise;
      }
      mPreviewAdvertiserId = advertiserId;
      mPreviewAdvertiserPromise = Advertiser.get({id: advertiserId}).$promise.then((data: any) => {
        ctrl.advertiserInfo = data;
        return data;
      }, () => advertiser);
      return mPreviewAdvertiserPromise;
    }));
  };

  const updateCreativeDims = () => {
    syncEditingModel();
    CreativeHelper.updateCreativeDims(ctrl, prevAssetAssignments);
  };

  const getLineItemInfo = () => {
    mLineItemPromise = LineItem.get({id: ctrl.editingModel.lineItem.id}, (lineItem: any) => {
      ctrl.lineItem = lineItem;
      ctrl.lineItemInfo = lineItem;
      updateWorkflowRestrictions(lineItem, _.get(lineItem, ['axProduct', 'lineItemWorkflowType'], 'DEFAULT'));
      ctrl.chartCurrency = _.get(lineItem, ['bidSpecification', 'cpm', 'currency'])
        || _.get(lineItem, ['bidSpecification', 'cpc', 'currency'])
        || _.get(lineItem, ['objectives', 'BUDGET', 'currency']);
      if (_.get(ctrl.lineItem, ['axProduct', 'id'])) {
        MarketplaceProduct.get({id: lineItem.axProduct.id}, (mp: any) => {
          marketplaceProduct = mp;
          updateWorkflowRestrictions(lineItem, mp.lineItemWorkflowType || 'DEFAULT');
          ctrl.optionalTargetTypes = _.isArray(mp.optionalTargetTypes) ? mp.optionalTargetTypes : [];
          Promise.resolve(mAllLayoutsPromise).then(() => {
            ctrl.layouts = _.filter(ctrl.layouts, (lay: any) => {
              return _.get(ctrl.editingModel, ['layout', 'id']) === lay.id || _.find(mp.layouts, (l: any) => l.id === lay.id);
            });
          });
          ctrl.hasAxProductDimensions = marketplaceProduct && Array.isArray(marketplaceProduct.allowedDimensions) && marketplaceProduct.allowedDimensions.length > 0;
          ctrl.getAllowedDimensions = marketplaceProduct.allowedDimensions.map((allowedDimension: any) => {
            return {
              label: allowedDimension[0] + ' x ' + allowedDimension[1],
              width: allowedDimension[0],
              height: allowedDimension[1]
            };
          });
          ctrl.selectedDimension = ctrl.getAllowedDimensions.find((dimension: any) => ctrl.editingModel.width === dimension.width && ctrl.editingModel.height === dimension.height);
          if (ctrl.hasAxProductDimensions) {
            ctrl.editingModel.dimensionType = 'FIXED';
          }
        });
      }
    }).$promise;
  };

  const checkHtmlUrls = () => {
    syncEditingModel();
    CreativeHelper.checkHtmlUrls(ctrl);
  };

  const matchConstraintsAndAssets = (newAssetUpload?: boolean) => {
    syncEditingModel();
    return CreativeHelper.matchConstraintsAndAssets(ctrl, mInitPromises, mLayoutAssetMatches, prevAssetAssignments, delayedPreview, newAssetUpload);
  };

  const updateEmailCode = () => {
    ctrl.emailCodeReason = null;
    const destUrl = _.find(ctrl.editingModel.cToUrls, (v: any) => !!v) || '';
    if (!destUrl) {
      ctrl.emailCodeReason = 'A creative tag for emails requires a destination URL to be entered.';
      return;
    }
    const assets = _.map(ctrl.editingModel.cToAssets, (a: any, key: string) => ({key: key, asset: a}));
    const imageIds = _.map(_.filter(ctrl.acsWithAssetMatches, (ac: any) => (MediaTypeHelper.getMediaType(ac.mimeTypes) as any).image), (ac: any) => ac.tag);
    const hasImage = !!_.find(assets, (asset: any) => imageIds.indexOf(asset.key) > -1 && _.get(asset, ['asset', 'id']));
    if (!hasImage) {
      ctrl.emailCodeReason = 'A creative tag for emails requires at least one image to be part of your creative.';
    }
  };

  const callForPreview = () => {
    Promise.all([Promise.resolve(mAllLayoutsPromise), Promise.resolve(mLineItemPromise)]).then(() => getAdvertiserForPreview()).then((previewAdvertiser: any) => {
      syncEditingModel();
      updateEmailCode();

      const previewResults = (failureReason: string | null, previewData?: any) => {
        // Defer — selPreview flips [hidden]="!selPreview" true→false mid-CD (NG0100).
        deferMarkForCheck(cdr, () => {
          ctrl.selPreviewFail = failureReason;
          ctrl.selPreview = previewData;
          const selLayoutType = _.get(ctrl.editingModel, ['layout', 'type'], 'unspecified');
          const key = ctrl.defaultLayoutTypes.indexOf(selLayoutType) > -1 && selLayoutType !== 'HTML' ? 'style1' : 'style2';
          if (previewData && ctrl.selPreviewTrigger[key].previewCreative) {
            ctrl.selPreviewTrigger[key].previewCreative(previewData);
          }
        });
      };

      if (!ctrl.editingModel.layout || !ctrl.editingModel.layout.id) {
        previewResults('A valid layout needs to be selected before a creative can be previewed.');
        return;
      }

      const adServingWarnings = _.find(ctrl.editingModel.layout.validationWarnings, (v: any) => v.preventsAdServing);
      if (adServingWarnings) {
        previewResults('The select layout is invalid and cannot be used to preview a creative.');
        return;
      }

      const creativeAssets = ctrl.editingModel.assets.concat(ctrl.editingModel.libraryCreativeAssets || []);
      const availableAssets = _.map(creativeAssets, (asset: any) => _.omit(asset, ['matchingAcs', 'assetComponents']));
      if (availableAssets.length === 0 && _.filter(ctrl.editingModel.layout.assetComponents, ['required', 'MANDATORY']).length > 0) {
        previewResults('Mandatory assets must be made available before a creative can be previewed.');
        return;
      }

      const assets = _.filter(availableAssets, (asset: any) => {
        const assetIds = _.keyBy(_.valuesIn(ctrl.editingModel.cToAssets), 'id');
        return assetIds[asset.id];
      });
      const text = _.filter(ctrl.editingModel.cToText, (v: any) => !!v);
      const links = _.filter(ctrl.editingModel.cToUrls, (v: any) => !!v);
      const choices = _.filter(ctrl.editingModel.cToChoices, (v: any) => !!v);
      const mandatoryCompLength = _.filter(_.get(ctrl.editingModel, ['layout', 'layoutComponents'], []), (lc: any) => lc.required === 'MANDATORY').length;
      if (mandatoryCompLength > 0 && assets.length === 0 && text.length === 0 && links.length === 0 && choices.length === 0) {
        previewResults('At least one layout component needs an asset or text assigned to it before a creative can be previewed.');
        return;
      }

      if (ctrl.editingModel.dimensionType === 'FIXED' && (!ctrl.editingModel.width || !ctrl.editingModel.height)) {
        previewResults('The creative must have a width and height before it can be previewed.');
        return;
      }
      if (ctrl.editingModel.dimensionType === 'FIXED_HEIGHT' && !ctrl.editingModel.height) {
        previewResults('The creative must have a height before it can be previewed.');
        return;
      }
      if (ctrl.editingModel.dimensionType === 'FIXED_WIDTH' && !ctrl.editingModel.width) {
        previewResults('The creative must have a width before it can be previewed.');
        return;
      }

      const creativeModel = _.cloneDeep(ctrl.editingModel);
      CreativeHelper.doLayoutParams(ctrl, creativeModel);
      previewResults(null, {
        layoutId: ctrl.editingModel.layout.id,
        creative: creativeModel,
        lineItem: ctrl.lineItem,
        advertiser: previewAdvertiser,
        election: _.get(ctrl.lineItem, 'election') || _.get(ctrl.editingModel, ['lineItem', 'election']) || _.get(ctrl.lineItemInfo, 'election') || _.get(ctrl.editingModel, 'election'),
        // for now the preview api in the ad server expects the video param even for vast audio
        video: requiresVideoPreviewParam(ctrl.editingModel)
      });
    });
  };

  const saveOverridableTargeting = () => {
    ctrl.editingModel.overridableTargeting = [];
    _.forEach(ctrl.tOverrides, (val: any, key: string) => {
      if (val) {
        ctrl.editingModel.overridableTargeting.push(key);
      }
    });
  };

  const overridableTargetingForCtrl = () => {
    ctrl.tOverrides = {};
    ctrl.tOverridesTypes = [];
    _.forEach(ADN_TARGETING_TYPES, (val: any) => {
      const ot = _.find(ctrl.editingModel.overridableTargeting, (tt: string) => tt === val.apiType);
      if (ot) {
        ctrl.tOverridesTypes.push(val.id);
      }
      ctrl.tOverrides[val.apiType] = !!ot;
    });
  };

  const checkShowGeneralVariables = () => {
    ctrl.showGeneralVariables = _.get(ctrl, ['editingModel', 'layout', 'layoutType']) === 'THIRD_PARTY' || !!_.find(ctrl.allComponents || [], (c: any) => c.type === 'TEXT' && c.textType === 'CODE');
  };

  const afterInitAfterSave = () => {
    syncEditingModel();
    // Save response replaces layout with a stub {id}; restore assetComponents etc. before CD.
    const layoutId = _.get(ctrl.editingModel, ['layout', 'id']);
    const layoutFromRef = layoutId && ctrl.layoutRef[layoutId];
    if (layoutFromRef) {
      ctrl.editingModel.layout = layoutFromRef;
    }
    ctrl.hasLibraryCreative = !!_.get(ctrl.editingModel.libraryCreative, ['id'], '');
    ctrl.lcOverrides = {};
    _.forEach(ctrl.editingModel.overridableLayoutParameters, (key: string) => {
      ctrl.lcOverrides[key] = true;
    });
    overridableTargetingForCtrl();
    ctrl.dimensionOverrides = _.indexOf(ctrl.editingModel.overrideFields, 'width') > -1 || _.indexOf(ctrl.editingModel.overrideFields, 'height') > -1 || _.indexOf(ctrl.editingModel.overrideFields, 'dimensionType') > -1;
    ctrl.savedTeams = _.clone(ctrl.editingModel.teams);
    ctrl.editingModel.renderOption = ctrl.editingModel.renderOption || null;

    ctrl.advertiserMismatch = findCreativeAdvertiserMismatch(ctrl.editingModel.validationWarnings);

    Promise.resolve(mAllLayoutsPromise).then(() => {
      const hydratedLayout = ctrl.layoutRef[_.get(ctrl.editingModel, ['layout', 'id'])];
      if (hydratedLayout) {
        ctrl.editingModel.layout = hydratedLayout;
      }
      const productId = _.get(ctrl.editingModel, ['layoutParameters', 'retailProductId']);
      const merchantId = _.get(ctrl.editingModel, ['layoutParameters', 'merchantId']);
      if (hydratedLayout && hydratedLayout.detectedCategory === 'RETAIL' && productId && merchantId) {
        hydrateSelectedRetailProduct(productId, merchantId);
        const notRetailComponents = _.filter(ctrl.layoutRef[ctrl.editingModel.layout.id].layoutComponents, (lc: any) => !ctrl.retailProductFields[lc.tag]);
        ctrl.isPureRetailCreative = ctrl.layoutRef[ctrl.editingModel.layout.id].layoutType === 'RETAIL' && !notRetailComponents.length;
      }
    });

    Promise.resolve(CreativeHelper.deriveFee(mLineItemPromise, ctrl, ctrl.editingModel.creativeFormatFee).then((fee: any) => {
      ctrl.editingModel.simplifiedCreativeFormatFee = fee;
    }));

    if (!ctrl.isCreative && ctrl.isPureMarketplace) {
      Promise.resolve(teamQuery).then(() => {
        ctrl.editingModel.singularTeam = ctrl.editingModel.teams[0];
        if (!ctrl.editingModel.singularTeam && ctrl.allTeams.length === 1) {
          ctrl.editingModel.singularTeam = _.get(ctrl, ['allTeams', 0, 'id']);
        }
      });
    }
    checkShowGeneralVariables();
  };

  const afterInit = () => {
    editPhase.wait('init');
    syncEditingModel();
    ctrl.editingModel = creativeType.caps === 'CREATIVE' ? new Creative(ctrl.editingModel) : new LibraryCreative(ctrl.editingModel);
    ctrl.model = ctrl.editingModel;
    ctrl.impTrackingUrls = _.map(ctrl.editingModel.impressionTrackingUrls, (url: string) => ({trackingUrl: url}));

    // for an AX_PUBLISHER viewing a creative, some parts of the creative will be suppressed
    const axPublisherPermission = ctrl.editingModel.permission === 'AX_PUBLISHER';
    ctrl.readOnlyAxPublisher = axPublisherPermission && !ctrl.editingModel.targeting;
    ctrl.showTargeting = !axPublisherPermission || ctrl.editingModel.targeting;
    ctrl.showExternalAdUnits = !axPublisherPermission || ctrl.editingModel.externalAdUnits;
    ctrl.showCpm = !axPublisherPermission || ctrl.editingModel.cpm;
    ctrl.blockSubmit = readOnly || ctrl.blockSubmit || ctrl.readOnlyAxPublisher;

    ctrl.titleKey = model ? 'creative.edit' : 'creative.add';
    ctrl.editingModel.targeting = ctrl.editingModel.targeting || {};
    ctrl.editingModel.cToAssets = ctrl.editingModel.cToAssets || {};
    ctrl.editingModel.userState = ctrl.editingModel.userState || 'APPROVED';
    ctrl.editingModel.type = ctrl.editingModel.type || 'INTERNAL';
    ctrl.htmlAcWithAssetMatches = {};
    ctrl.selPreviewTrigger = {style1: {}, style2: {}};
    ctrl.editingModel.teams = _.map(ctrl.editingModel.teams, 'id');

    ctrl.isLockedDown = false;
    ctrl.isCreativeContentLocked = false;
    ctrl.showStats = !axPublisherPermission;

    ctrl.editingModel.dimensionType = ctrl.editingModel.dimensionType || 'FIXED';

    if (!_.get(lineItemInfo, ['id']) && !_.get(ctrl.editingModel, ['lineItem', 'id']) && !_.get(ctrl.editingModel, ['creativeSet', 'id'])) {
      ctrl.orphan = true;
    }

    const advertiser = advertiserFromNavigation(navigationHistory, routeParams);
    if (advertiser) {
      ctrl.editingModel.advertiser = advertiser;
      ctrl.advertiserSpecified = true;
    }
    if (ctrl.editingModel.advertiser && ctrl.orphan) {
      ctrl.advertiserSpecified = true;
    }

    if (_.get(ctrl.editingModel, ['lineItem', 'id'])) {
      getLineItemInfo();
    }

    if (!mAllLayoutsPromise) {
      mAllLayoutsPromise = CreativeHelper.getLayouts(ctrl, defaultLayoutType, mLineItemPromise);
    }
    mInitPromises.push(mAllLayoutsPromise);
    if (ctrl.editingModel.id) {
      ctrl.models = [ctrl.editingModel];
      Promise.resolve(mAllLayoutsPromise).then(() => {
        if (ctrl.editingModel.layout) {
          const hydratedLayout = _.find(ctrl.layouts, ['id', ctrl.editingModel.layout.id]);
          if (hydratedLayout) {
            ctrl.editingModel.layout = hydratedLayout;
          }
        }
      });
    }
    Promise.all(mInitPromises).then(() => {
      return Promise.resolve(matchConstraintsAndAssets()).then(() => {
        delayedPreview();
        checkShowGeneralVariables();
      });
    }).then(() => {
      editPhase.done('init');
    }, () => {
      editPhase.done('init');
    });

    if (ctrl.externalAdUnitsHook.initExtAdUnits) {
      ctrl.externalAdUnitsHook.initExtAdUnits();
    }
    afterInitAfterSave();
  };

  const afterSave = (creative: any) => {
    const suppressLineItemTransition = suppressLineItemTransitionOnce;
    suppressLineItemTransitionOnce = false;

    syncEditingModel();
    ctrl.targetingResult = {data: false};
    ctrl.isNew = false;
    ctrl.editingModel.targeting = creative.targeting || {};
    ctrl.hasLibraryCreative = !!_.get(creative.libraryCreative, ['id'], '');

    ctrl.teamsWarning = (_.get(ctrl.editingModel, 'teams') || []).length !== (_.get(creative, 'teams') || []).length;
    _.set(ctrl.editingModel, 'teams', _.map(_.get(creative, 'teams') || [], 'id'));

    if (_.isFunction(ctrl.eventHook.onCreativeSave)) {
      ctrl.eventHook.onCreativeSave();
    }
    if (ctrl.toLineItem && !suppressLineItemTransition && _.get(creative, ['lineItem', 'id'])) {
      $state.go('app.adv.line-items.line-item', {id: creative.lineItem.id});
      return;
    }
    if (ctrl.toCreativeSet && _.get(creative, ['creativeSet', 'id'])) {
      $state.go('app.adv.creative-sets.creative-set', {id: creative.creativeSet.id});
      return;
    }
    if (_.get(ctrl.editingModel, ['lineItem', 'id']) && ctrl.orphan) {
      getLineItemInfo();
      ctrl.postOrphan = true;
    }
    if (ctrl.externalAdUnitsHook.initExtAdUnits) {
      ctrl.externalAdUnitsHook.initExtAdUnits();
    }
    afterInitAfterSave();
    delayedPreview();
  };

  ctrl._creativeAfterInit = afterInit;
  ctrl._creativeAfterInitAfterSave = afterInitAfterSave;
  ctrl._creativeAfterSave = afterSave;

  ctrl.lineItemInfo = lineItemInfo;
  ctrl.toLineItem = !!lineItemInfo.fromLineItem;
  ctrl.toCreativeSet = !!lineItemInfo.fromCreativeSet;

  ctrl.externalAdUnitsHook = {
    editsMade: () => ctrl.editsMade(),
    get model() {
      return ctrl.editingModel;
    }
  };

  ctrl.cancelShareOfVoice = () => {
    ctrl.editingModel.sponsorshipPercentage = undefined;
    ctrl.editsMade();
  };

  ctrl.breakLink = () => {
    syncEditingModel();
    const libCreative = _.cloneDeep(ctrl.editingModel);
    libCreative.libraryCreative = null;
    ctrl.pending = true;
    ctrl.isDelinking = true;
    libCreative.savePartial('libraryCreative').then((creative: any) => {
      ctrl.editingModel.libraryCreative = creative.libraryCreative;
      ctrl.saveDirect(creative);
    }).finally(() => {
      ctrl.pending = false;
      ctrl.isDelinking = false;
    });
  };

  ctrl.createRetailCreative = () => {
    ctrl.editsMade();
    const merchantGuide = LocalNetworkInfo.obtain().getMerchantGuide();
    creativeRetailProductModalService.open({
      selRetailProduct: ctrl.selRetailProduct,
      onSelect: (product: any) => {
        const merchantLink = _.get(merchantGuide, [product.merchantId, 'link']);
        ctrl.pending = true;
        return ensureRetailCreativeIsSaved().then((savedCreative: any) => {
          const lineItemId = _.get(savedCreative, ['lineItem', 'id']) || _.get(ctrl.editingModel, ['lineItem', 'id']);
          const creativeId = _.get(savedCreative, 'id') || _.get(ctrl.editingModel, 'id') || '';
          return Creative.retailAd(lineItemId, creativeId, _.get(ctrl.editingModel, ['layout', 'id']), merchantLink, product.itemId);
        })
          .then(Creative.hydrate)
          .then((creative: any) => {
            applyRetailCreativeUpdate(creative);
            return hydrateSelectedRetailProduct(product.itemId, product.merchantId);
          })
          .finally(() => {
            ctrl.pending = false;
          });
      }
    });
  };

  ctrl.assetAssignmentChanged = (tag: string) => {
    syncEditingModel();
    const selectedAsset = _.get(ctrl.editingModel, 'cToAssets.' + tag);
    if (selectedAsset && MediaTypeHelper.isMediaType(selectedAsset.mimeType, MEDIA_TYPE.html) && ctrl.primaryHtmlAsset) {
      ctrl.primaryHtmlAsset = selectedAsset;
      checkHtmlUrls();
    }
    updateCreativeDims();
    delayedPreview();
    updateDefaultName();
    if (_.isFunction(ctrl.eventHook.onAssetUpdate)) {
      ctrl.eventHook.onAssetUpdate();
    }
  };

  ctrl.selectTeams = () => {
    ctrl.editsMade();
    ctrl.editingModel.teams = _.map(ctrl.allTeams, 'id');
  };

  ctrl.deselectTeams = () => {
    ctrl.editsMade();
    ctrl.editingModel.teams = [];
  };

  ctrl.selectLayout = (layout?: any) => {
    syncEditingModel();
    if (layout) {
      ctrl.editingModel.layout = layout;
    }
    ctrl.editingModel.cToAssets = {};
    // ngModelChange can fire with null when the layout select is cleared.
    if (!ctrl.editingModel.layout) {
      ctrl.editingModel.defaultLayoutWidth = null;
      ctrl.editingModel.defaultLayoutHeight = null;
      return;
    }
    if (ctrl.editingModel.defaultLayoutWidth === ctrl.editingModel.width && ctrl.editingModel.defaultLayoutHeight === ctrl.editingModel.height) {
      ctrl.editingModel.width = ctrl.editingModel.layout.defaultWidth;
      ctrl.editingModel.height = ctrl.editingModel.layout.defaultHeight;
    }
    ctrl.editingModel.width = ctrl.editingModel.width || ctrl.editingModel.layout.defaultWidth;
    ctrl.editingModel.height = ctrl.editingModel.height || ctrl.editingModel.layout.defaultHeight;
    ctrl.editingModel.defaultLayoutWidth = ctrl.editingModel.layout.defaultWidth;
    ctrl.editingModel.defaultLayoutHeight = ctrl.editingModel.layout.defaultHeight;
    Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
      checkShowGeneralVariables();
      callForPreview();
    });
  };

  ctrl.addImpressionTrackingUrl = () => {
    ctrl.impTrackingUrls = ctrl.impTrackingUrls || [];
    ctrl.impTrackingUrls.push({trackingUrl: null});
  };

  ctrl.eventHook = {
    onUpdate: () => {
      ctrl.editsMade();
      matchConstraintsAndAssets(true);
    },
    assignAsset: (asset: any, tag: string) => {
      ctrl.editsMade();
      ctrl.editingModel.cToAssets[tag] = asset;
      delayedPreview();
    }
  };

  ctrl.componentValueUpdated = () => {
    ctrl.editsMade();
    delayedPreview();
  };

  ctrl.dimensionsChange = () => {
    delayedPreview();
  };

  ctrl.editTarget = () => {
    ctrl.targetingResult = {data: false};
    const productType = _.get(ctrl.editingModel, ['axProduct', 'type']);
    const lineItemType = (_.get(marketplaceProduct, ['type']) === 'POLITICAL' || productType === 'POLITICAL' || _.get(ctrl.lineItem, ['election', 'id']) || _.get(ctrl.editingModel, ['layout', 'political'])) ? 'POLITICAL' : 'NORMAL';
    const modelLineItem = _.cloneDeep(ctrl.lineItem) || {};
    modelLineItem.creativesForReach = [ctrl.editingModel];
    modelLineItem.fromCreative = true;
    let availableTargets: any = null;
    if (_.isArray(ctrl.optionalTargetTypes) && marketplaceProduct) {
      const targetIds = _.map(_.filter(ADN_TARGETING_TYPES, (tt: any) => ctrl.optionalTargetTypes.indexOf(tt.apiType) > -1), 'id');
      availableTargets = _.pick(ADN_TARGETING_TYPES, targetIds);
    }

    TargetingEditorModalService.open({
      lineItemType: lineItemType,
      modelLineItem: modelLineItem,
      pageType: 'creative',
      reachEnabled: 'CREATIVE',
      targetingMode: null,
      overridableTargeting: ctrl.hasLibraryCreative ? ctrl.tOverrides : null,
      mandatoryTargets: undefined,
      availableTargets: availableTargets,
      targeting: _.cloneDeep(ctrl.editingModel.targeting),
      isLockedDown: ctrl.isLockedDown
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      const fieldsForSaving = ['targeting'];
      if (wc.overrides) {
        ctrl.tOverrides = wc.overrides;
        saveOverridableTargeting();
        fieldsForSaving.push('overridableTargeting');
      }
      _.assign(ctrl.editingModel.targeting, wc.wc);
      _.assign(ctrl.editingModel.overridableTargeting, wc.overrides);
      ctrl.editingModel.targeting = _.cloneDeep(ctrl.editingModel.targeting);
      if (!ctrl.isNew) {
        overridableTargetingForCtrl();
        ctrl.editingModel.savePartial(fieldsForSaving).then((respCreative: any) => {
          ctrl.targetingResult.data = true;
          _.set(ctrl.editingModel, 'validationWarnings', respCreative.validationWarnings);
          _.set(ctrl.editingModel, 'targeting', respCreative.targeting);
        }, (err: any) => {
          ctrl.targetingResult.errors = _.get(err, ['data', 'errors']);
          Rollbar.error('Creative targeting problem', err);
        });
      } else {
        ctrl.editsMade();
      }
    }, () => undefined);
  };

  ExternalDemandSource.query().$promise.then((connPage: any) => {
    ctrl.extDemandSources = connPage.results;
  });

  ctrl.refreshCodeMirrorVar = false;
  ctrl.refreshCodeMirror = () => {
    setTimeout(() => {
      ctrl.refreshCodeMirrorVar = true;
    }, 100);
  };

  modelComms.addSubscriber((data: any, type: any) => {
    if (data && type === OBJECT_TYPE.LineItem) {
      ctrl.lineItemInfo = data;
    } else if (data && type === OBJECT_TYPE.Advertiser) {
      ctrl.advertiserInfo = data;
    }
  });

  ctrl._creativeOnBeforeSubmit = () => {
    syncEditingModel();
    if (ctrl.unreject && ctrl.editingModel.userState === 'REJECTED') {
      ctrl.editingModel.userState = 'APPROVED';
    }
    if (!_.isFinite(ctrl.editingModel.sponsorshipPercentage)) {
      ctrl.editingModel.sponsorshipPercentage = null;
    }
    // remove any empty-string impression-tracking urls.
    ctrl.editingModel.impressionTrackingUrls = _.map(_.filter(ctrl.impTrackingUrls, (url: any) => url.trackingUrl && url.trackingUrl.length > 0), 'trackingUrl');

    _.forEach(_.keysIn(ctrl.editingModel.cToAssets), (t: string) => {
      if (!ctrl.editingModel.cToAssets[t]) {
        delete ctrl.editingModel.cToAssets[t];
      }
    });

    ctrl.editingModel.overridableLayoutParameters = [];
    _.forEach(ctrl.lcOverrides, (val: any, key: string) => {
      if (val) {
        ctrl.editingModel.overridableLayoutParameters.push(key);
      }
    });
    saveOverridableTargeting();
    ctrl.editingModel.overrideFields = [];
    if (ctrl.dimensionOverrides) {
      ctrl.editingModel.overrideFields.push('width', 'height', 'dimensionType');
    }

    if (ctrl.editingModel.dimensionType === 'FLEXIBLE') {
      ctrl.editingModel.width = null;
      ctrl.editingModel.height = null;
    } else if (ctrl.editingModel.dimensionType === 'FIXED_WIDTH') {
      ctrl.editingModel.height = null;
    } else if (ctrl.editingModel.dimensionType === 'FIXED_HEIGHT') {
      ctrl.editingModel.width = null;
    }

    CreativeHelper.doLayoutParams(ctrl);
    if (_.isEmpty(ctrl.editingModel.targeting)) {
      // this is to avoid an API bug
      delete ctrl.editingModel.targeting;
    }
    if (ctrl.primaryHtmlAsset) {
      Asset.update(ctrl.editingModel.id, {htmlUrls: ctrl.primaryHtmlAsset.htmlUrls}, ctrl.primaryHtmlAsset.id);
    }

    if ((_.get(ctrl.editingModel, ['cpm', 'amount']) || 0) <= 0) {
      // This will delete the CPM on the server
      ctrl.editingModel.cpm = null;
    }

    if (ctrl.isPureMarketplace && !ctrl.isCreative) {
      ctrl.editingModel.teams = ctrl.editingModel.singularTeam ? [ctrl.editingModel.singularTeam] : null;
    }

    if (ctrl.externalAdUnitsHook.beforeSubmitExtAdUnit) {
      ctrl.externalAdUnitsHook.beforeSubmitExtAdUnit();
    }
  };

  ctrl.selectDimension = () => {
    if (ctrl.selectedDimension) {
      ctrl.editingModel.width = ctrl.selectedDimension.width;
      ctrl.editingModel.height = ctrl.selectedDimension.height;
      delayedPreview();
    }
  };

  ctrl.filterAssets = (assets: any[]) => {
    return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
  };

  ctrl.translateMediaTypes = (mimeTypes: any) => {
    return formatMediaTypesTranslate(mimeTypes, (key) => translate.instant(key));
  };

  ctrl.mediaTypeLabel = (mimeType: any) => {
    return translate.instant(formatMediaTypeKey(mimeType));
  };

  ctrl.ajsResourceFactory = () => ({
    model: ctrl.editingModel,
    isNew: ctrl.isNew,
    eventHook: ctrl.eventHook
  });
}
